/**
 * Eterna Vaults - MFA Verification Component
 * 
 * This component handles MFA verification during login.
 */

import React, { useState, useEffect, useRef } from 'react';
import Icon from '../Icons';
import './styles.css';

const MFAVerification = ({ 
  member, 
  deviceId, 
  deviceName, 
  onSuccess, 
  onCancel,
  onUseBackupCode 
}) => {
  const [token, setToken] = useState('');
  const [backupCode, setBackupCode] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [useBackupCode, setUseBackupCode] = useState(false);
  const [trustDevice, setTrustDevice] = useState(true);
  const [timeLeft, setTimeLeft] = useState(30);
  const [isResending, setIsResending] = useState(false);
  
  const tokenInputRef = useRef(null);
  const backupCodeInputRef = useRef(null);

  const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api.eternavaults.com';

  useEffect(() => {
    if (tokenInputRef.current && !useBackupCode) {
      tokenInputRef.current.focus();
    }
    if (backupCodeInputRef.current && useBackupCode) {
      backupCodeInputRef.current.focus();
    }
  }, [useBackupCode]);

  useEffect(() => {
    // Auto-focus and clear error when switching modes
    setError('');
    if (useBackupCode) {
      setToken('');
    } else {
      setBackupCode('');
    }
  }, [useBackupCode]);

  useEffect(() => {
    // Countdown timer for token refresh
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        if (prev <= 1) {
          return 30; // Reset to 30 seconds
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  const handleTokenChange = (e) => {
    const value = e.target.value.replace(/\D/g, '').slice(0, 6);
    setToken(value);
    setError('');
    
    // Auto-submit when 6 digits are entered
    if (value.length === 6) {
      handleVerify();
    }
  };

  const handleBackupCodeChange = (e) => {
    const value = e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '').slice(0, 8);
    setBackupCode(value);
    setError('');
    
    // Auto-submit when 8 characters are entered
    if (value.length === 8) {
      handleVerify();
    }
  };

  const handleVerify = async () => {
    if (useBackupCode) {
      if (!backupCode || backupCode.length !== 8) {
        setError('Please enter a valid backup code');
        return;
      }
    } else {
      if (!token || token.length !== 6) {
        setError('Please enter a valid 6-digit code');
        return;
      }
    }
    
    setLoading(true);
    setError('');
    
    try {
      const response = await fetch(`${API_BASE_URL}/api/mfa/${member._id}/verify-login`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          token: useBackupCode ? backupCode : token,
          deviceId,
          deviceName,
          trustDevice
        })
      });
      
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.error || 'MFA verification failed');
      }
      
      // Success - call onSuccess with the member data
      onSuccess(data.member);
    } catch (err) {
      console.error('MFA verification error:', err);
      setError(err.message);
      setLoading(false);
      
      // Clear the input fields
      if (useBackupCode) {
        setBackupCode('');
      } else {
        setToken('');
      }
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    // In a real implementation, you might send a new code via SMS
    // For now, we'll just show a message
    setTimeout(() => {
      setIsResending(false);
      setError('New code sent to your authenticator app');
    }, 1000);
  };

  const toggleMode = () => {
    setUseBackupCode(!useBackupCode);
    setError('');
  };

  return (
    <div className="mfa-verification-modal">
      <div className="mfa-verification-content">
        <button className="mfa-close-button" onClick={onCancel}>
          ×
        </button>
        
        <div className="mfa-verification-header">
          <Icon name="shield" size={48} />
          <h2>Two-Factor Authentication</h2>
          <p>Enter the code from your authenticator app</p>
        </div>

        <div className="mfa-member-info">
          <Icon name="user" size={24} />
          <span>{member.name}</span>
        </div>

        {deviceName && (
          <div className="mfa-device-info">
            <Icon name="monitor" size={20} />
            <span>Signing in from {deviceName}</span>
          </div>
        )}

        <div className="mfa-timer">
          <Icon name="clock" size={16} />
          <span>Code refreshes in {timeLeft}s</span>
        </div>

        {!useBackupCode ? (
          <div className="mfa-token-section">
            <div className="mfa-token-input">
              <input
                ref={tokenInputRef}
                type="text"
                value={token}
                onChange={handleTokenChange}
                placeholder="000000"
                maxLength="6"
                className="mfa-token-field"
                disabled={loading}
              />
            </div>
            
            <div className="mfa-token-actions">
              <button 
                className="mfa-link-btn"
                onClick={toggleMode}
                disabled={loading}
              >
                Use backup code instead
              </button>
            </div>
          </div>
        ) : (
          <div className="mfa-backup-section">
            <div className="mfa-backup-input">
              <input
                ref={backupCodeInputRef}
                type="text"
                value={backupCode}
                onChange={handleBackupCodeChange}
                placeholder="BACKUP1"
                maxLength="8"
                className="mfa-backup-field"
                disabled={loading}
              />
            </div>
            
            <div className="mfa-backup-actions">
              <button 
                className="mfa-link-btn"
                onClick={toggleMode}
                disabled={loading}
              >
                Use authenticator app instead
              </button>
            </div>
          </div>
        )}

        {error && (
          <div className="mfa-error">
            <Icon name="alert-circle" size={20} />
            <span>{error}</span>
          </div>
        )}

        <div className="mfa-trust-device">
          <label className="mfa-checkbox">
            <input
              type="checkbox"
              checked={trustDevice}
              onChange={(e) => setTrustDevice(e.target.checked)}
              disabled={loading}
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">
              Trust this device for 30 days
            </span>
          </label>
        </div>

        <div className="mfa-verification-actions">
          <button 
            className="mfa-btn secondary"
            onClick={onCancel}
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            className="mfa-btn primary"
            onClick={handleVerify}
            disabled={loading || (useBackupCode ? backupCode.length !== 8 : token.length !== 6)}
          >
            {loading ? 'Verifying...' : 'Verify'}
          </button>
        </div>

        <div className="mfa-help">
          <p>Having trouble?</p>
          <button 
            className="mfa-link-btn"
            onClick={handleResendCode}
            disabled={isResending}
          >
            {isResending ? 'Sending...' : 'Resend code'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default MFAVerification;
